import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import type {
  CodingResult,
  UpstreamApiCalculatedCodingResult,
} from 'src/types/dashboard';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';

import useDashboardFilters from './useDashboardFilters';

const mapCodingResults = (
  upstreamCodingResults: UpstreamApiCalculatedCodingResult[],
) =>
  upstreamCodingResults.map((upstreamCodingResult) => ({
    categoryLabel: upstreamCodingResult.category_label,
    categoryLabelEn: upstreamCodingResult.category_label_en,
    countNegative: upstreamCodingResult.count_negative,
    countNeutral: upstreamCodingResult.count_neutral,
    countPositive: upstreamCodingResult.count_positive,
    relevanceAll: upstreamCodingResult.relevance_all,
    relevanceNegative: upstreamCodingResult.relevance_negative,
    relevanceNeutral: upstreamCodingResult.relevance_neutral,
    relevancePositive: upstreamCodingResult.relevance_positive,
    color: upstreamCodingResult.color,
    secondaryColor: upstreamCodingResult.secondary_color,
    topics: upstreamCodingResult.topics.map((topic) => ({
      topicLabel: topic.topic_label,
      topicLabelEn: topic.topic_label_en,
      color: topic.color,
      secondaryColor: topic.secondary_color,
      sentimentCode: topic.sentiment_code,
      sentimentLabel: topic.sentiment_label,
      sentimentLabelEn: topic.sentiment_label_en,
      relevance: topic.relevance,
      id: topic.id,
      count: topic.count,
      linkage: topic.linkage,
    })),
    topicsTotal:
      upstreamCodingResult.topics_total?.map((topic) => ({
        topicLabel: topic.topic_label,
        color: topic.color,
        secondaryColor: topic.secondary_color,
        id: topic.id,
        count: topic.count,
        linkage: topic.linkage,
      })) || [],
  }));

const fetchCodingResults = async (
  endpoint: string,
  language: string,
  filters: Record<string, string[]>,
  question: string | null,
  textFilter?: string,
  signal?: AbortSignal,
): Promise<CodingResult[]> =>
  HttpService.post(
    endpoint,
    {
      language,
      filters,
      question: question || '',
      text_filter: textFilter || '',
    },
    undefined,
    undefined,
    { signal },
  )
    .then((data) => Promise.resolve(mapCodingResults(data.data)))
    .catch(({ response }) => Promise.reject(response.status));

const useCodingResultsQuery = (
  enable: boolean,
  question: string | null,
  dashboardId?: number,
  textFilter?: string,
) => {
  const { flatFiltersDebounced } = useDashboardFilters();
  const { i18n } = useTranslation();

  const endpoint = useMemo(
    () => generatePath(apiPaths.DASHBOARD.CODING_RESULTS, { id: dashboardId }),
    [dashboardId],
  );

  const flatFiltersLength = Object.keys(flatFiltersDebounced).length;

  return useQuery<CodingResult[], number>({
    queryKey: [
      endpoint,
      i18n.language,
      flatFiltersDebounced,
      question,
      textFilter,
    ],
    queryFn: ({ signal }) =>
      fetchCodingResults(
        endpoint,
        i18n.language,
        flatFiltersDebounced,
        question,
        textFilter,
        signal,
      ),
    enabled: enable && !!dashboardId && flatFiltersLength > 0,
  });
};

export default useCodingResultsQuery;
